import { APP_ENV, APP_RELEASE } from "./config";
import { CATALOGUE, EVENTS, SIGNUP_REASONS } from "./events";
import consoleDestination from "./destinations/console";
import hotjarDestination from "./destinations/hotjar";
import mixpanelDestination from "./destinations/mixpanel";
import { clearEntryReason, peekEntryReason, stashEntryReason } from "./pending";
import { routeTemplate } from "./routes";
import { warn } from "./warn";

const ALL_DESTINATIONS = [
  consoleDestination,
  hotjarDestination,
  mixpanelDestination
];

// defaults to all-granted
const preferences = { analytics: true, replay: true, marketing: true };

let destinations = null;

// traits from the last identify that ride along on every event; null = guest
let identity = null;

// id + traits of the last identify, so repeat calls with nothing new are skipped
let identifiedAs = null;

const call = (destination, method, ...args) => {
  try {
    destination[method](...args);
  } catch (e) {
    warn(`${destination.name}.${method} failed`, e);
  }
};

const getDestinations = () => {
  if (!destinations) {
    destinations = ALL_DESTINATIONS.filter(d => d.enabled());
    destinations.forEach(d => call(d, "init"));
  }
  return destinations;
};

const allowed = destination => preferences[destination.preference] !== false;

const commonProperties = () => ({
  app_env: APP_ENV,
  app_release: APP_RELEASE,
  is_guest: !identity,
  route: routeTemplate(window.location.pathname),
  ...(identity && {
    is_internal: identity.is_internal,
    paid_user: identity.paid_user,
    team_enterprise: identity.team_enterprise
  })
});

// called once at startup so destinations that record from page load don't wait for first event
export const initAnalytics = () => {
  getDestinations();
};

export const setTrackingPreferences = next => {
  Object.assign(preferences, next);
};

const sendEvent = (name, props, options) => {
  const event = Object.prototype.hasOwnProperty.call(CATALOGUE, name)
    ? CATALOGUE[name]
    : null;
  if (!event) {
    warn(`unknown event "${name}" — add it to src/analytics/events.js`);
    return;
  }

  const missing = (event.required || []).filter(key => props[key] == null);
  if (missing.length) {
    warn(`"${name}" is missing required properties:`, missing);
  }

  const payload = { ...commonProperties(), ...props };
  getDestinations()
    .filter(
      d =>
        allowed(d) &&
        (d.receivesAllEvents || (event.destinations || []).includes(d.name))
    )
    .forEach(d => call(d, "track", name, payload, event, options));
};

export const track = (name, props = {}, options = {}) => {
  try {
    sendEvent(name, props, options);
  } catch (e) {
    warn(`track("${name}") failed`, e);
  }
};

// reports a prompt and remembers it as the entry reason for `signup_completed`
export const trackSignupPrompt = prompt => {
  if (!prompt) return;

  const { reason, surface } = prompt;
  track(EVENTS.signup_prompted, prompt);

  if (reason === SIGNUP_REASONS.FORM_CROSS_LINK && peekEntryReason()) return;

  stashEntryReason({ reason, surface });
};

export const identify = (userId, traits = {}) => {
  if (userId == null) return;
  const key = JSON.stringify([String(userId), traits]);
  // skip if the same as the last identify
  if (key === identifiedAs) return;
  identifiedAs = key;
  identity = traits;
  getDestinations()
    .filter(allowed)
    .forEach(d => call(d, "identify", String(userId), traits));
};

export const reset = () => {
  // not preference-gated, clearing identity is always safe
  identity = null;
  identifiedAs = null;
  getDestinations().forEach(d => call(d, "reset"));
};

export const page = (props = {}) => {
  const payload = { ...commonProperties(), ...props };
  getDestinations()
    .filter(allowed)
    .forEach(d => call(d, "page", payload));
};

// entry reasons that are funnels of their own; anything else counts as organic
const SIGNUP_PATHS = [
  SIGNUP_REASONS.POST_ORDER,
  SIGNUP_REASONS.POST_SUBSCRIPTION
];

export const trackSignupCompleted = (method, fromCheckout = false) => {
  const entry = peekEntryReason();
  const entryPath = SIGNUP_PATHS.includes(entry?.reason)
    ? entry.reason
    : "organic";

  track(EVENTS.signup_completed, {
    method,
    signup_path: fromCheckout ? SIGNUP_REASONS.POST_SUBSCRIPTION : entryPath,
    ...(entry && { entry_reason: entry.reason })
  });
  clearEntryReason();
};
